import CheckIcon from '@mui/icons-material/Check';
import DoNotDisturbIcon from '@mui/icons-material/DoNotDisturb';
import FilterTiltShiftIcon from '@mui/icons-material/FilterTiltShift';
import TimelineIcon from '@mui/icons-material/Timeline';
import { MenuItem, Typography, Link } from '@mui/material';
import classNames from 'classnames';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import _ from 'lodash';
import type { FC } from 'react';
import { useNavigate, useParams } from 'react-router';
import { useMedia } from 'react-use';

import { requestConstants } from '../../../../constants/appConstants';
import { hasUnreadMessagesClassName } from '../../../../material-ui-theme/components/muiMenuItemOverrides';
import { useFormattedDateLocale } from '../../../../utils/useFormattedDateLocale';
import { useZustand } from '../../../../zustandStore';
import type { RequestType } from '../../../../zustandStore/slices';

dayjs.extend(relativeTime);

type RequestMenuItemProps = {
	request: RequestType & { unread?: boolean };
};

function getRequestStatusIcon(status: string) {
	switch (status) {
		case 'approved': {
			return <CheckIcon ts-variant="requestStatusIcon" className={status} />;
		}
		case 'pending': {
			return <TimelineIcon ts-variant="requestStatusIcon" className={status} />;
		}
		case 'error':
		case 'declined': {
			return <DoNotDisturbIcon ts-variant="requestStatusIcon" className={status} />;
		}
		default: {
			return <FilterTiltShiftIcon ts-variant="requestStatusIcon" className={status} />;
		}
	}
}

const getStatusLabel = (state: RequestType['state']) =>
	_.get(requestConstants.status, state.toLowerCase(), requestConstants.status.default).toLowerCase();

const RequestMenuItem: FC<RequestMenuItemProps> = ({ request }) => {
	const date = useFormattedDateLocale(request.lastEdit);
	const statusLabel = getStatusLabel(request.state);
	const { requestId } = useParams<{ requestId: string }>();
	const isMobileWidth = useMedia('(max-width: 650px)');
	const { openSideMenuOnDesktopView, openSideMenuOnMobileView } = useZustand();
	const navigate = useNavigate();

	return (
		<MenuItem
			data-testid="sideMenu-request-menu-item"
			ts-variant="sideMenuPanelOption"
			className={classNames({
				[`${hasUnreadMessagesClassName}`]: request.unread && requestId !== request.requestId,
				active: requestId === request.requestId,
			})}
			onClick={() => {
				void navigate(`/requests/${request.requestId}`);

				if (isMobileWidth) {
					openSideMenuOnDesktopView(false);
					openSideMenuOnMobileView(false);
				}
			}}
		>
			<Link ts-variant="requestMenuItemLink">
				{getRequestStatusIcon(statusLabel)}
				<Typography
					component="span"
					ts-variant="myRequestSpan"
					sx={{
						display: 'inline',
					}}
				>
					{request.title || 'New request'}
				</Typography>
				<Typography title={date} ts-variant="myRequestSecondaySpan">
					{dayjs(request.lastEdit).fromNow()}
				</Typography>
			</Link>
		</MenuItem>
	);
};

export default RequestMenuItem;
